import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Camera, ArrowLeft, Package, User, CheckCircle2, ScanLine, Keyboard,
  Trash, ImagePlus, MapPin, ChevronDown, Users, Send, MessageSquare, Mail,
  RefreshCw,
} from "lucide-react";
import { Card, Btn, Input } from "@/components/admin/ui";
import { ScannerModal } from "@/components/admin/ScannerModal";
import { ConversationPanel } from "@/components/admin/ConversationPanel";
import { API, BACKEND_URL } from "@/config";
import { formatSlot } from "@/utils/slotLabel";
import { fmtErr } from "@/context/AuthProvider";
import { useAuth } from "@/context/AuthProvider";
import { compressFiles } from "@/utils/compressImage";

/* ==================== Admin: Driver Scan ====================
 * Universal mobile-friendly scanner for delivery + pickup runs.
 *
 * Modes (?mode=): auto (default) · delivery · pickup
 *   - auto     — scan anything, auto-suggest next action
 *   - delivery — enforces the drop-off workflow: scan each unit, snap a
 *                photo, pick where you left it, then mark delivered
 *   - pickup   — scan a unit's barcode → mark it returned
 */

const DELIVERY_LOCATIONS = [
  { value: "customer",      label: "Customer" },
  { value: "bell_desk",     label: "Bell Desk" },
  { value: "hotel_lobby",   label: "Hotel Lobby" },
  { value: "door",          label: "Door" },
  { value: "friend_family", label: "Friend or Family Member" },
];

/* ==================== ScanPhotos ======================================= */
const ScanPhotos = ({ reservation, mode, onChange }) => {
  const [photos, setPhotos] = useState(reservation.photos || []);
  const [busy, setBusy] = useState(false);
  const cameraRef  = useRef(null);
  const galleryRef = useRef(null);

  const kind  = mode === "pickup" ? "return" : "delivery";
  const label = kind === "delivery" ? "Drop-off photos" : "Return / damage photos";
  const requiredNote = kind === "delivery"
    ? "At least one photo is required before Mark delivered."
    : "Snap any damage or dirt for the paper trail.";

  useEffect(() => { setPhotos(reservation.photos || []); }, [reservation.id, reservation.photos]);

  const upload = async (files) => {
    if (!files?.length) return;
    setBusy(true);
    try {
      const compressed = await compressFiles(files);
      const fd = new FormData();
      compressed.slice(0, 4).forEach((f) => fd.append("files", f));
      const { data } = await axios.post(
        `${API}/admin/reservations/${reservation.id}/photos?kind=${kind}`,
        fd, { headers: { "Content-Type": "multipart/form-data" } },
      );
      const next = [...photos, ...data.photos];
      setPhotos(next);
      onChange?.(next);
      toast.success(`${data.photos.length} photo${data.photos.length > 1 ? "s" : ""} captured ✓`);
    } catch (e) { toast.error(fmtErr(e.response?.data?.detail) || "Upload failed"); }
    finally { setBusy(false); }
  };

  const remove = async (photoId) => {
    if (!window.confirm("Delete this photo?")) return;
    try {
      await axios.delete(`${API}/admin/reservations/${reservation.id}/photos/${photoId}`);
      const next = photos.filter((p) => p.id !== photoId);
      setPhotos(next);
      onChange?.(next);
      toast.success("Removed");
    } catch (e) { toast.error(fmtErr(e.response?.data?.detail)); }
  };

  const list   = photos.filter((p) => p.kind === kind);
  const canAdd = list.length < 4;

  return (
    <div className={`mb-4 p-3 rounded-lg border ${kind === "delivery" && list.length === 0 ? "border-[#F4A93A]/60 bg-[#F4A93A]/5" : "border-[#FFD166]/25 bg-black/30"}`} data-testid="scan-photos">
      <div className="flex items-center justify-between mb-2">
        <div>
          <div className="text-xs font-medium text-[#F6F1E4]">{label} <span className="text-[#B9C8D5] text-[10px]">({list.length}/4)</span></div>
          <div className="text-[10px] text-[#B9C8D5]">{requiredNote}</div>
        </div>
        {canAdd && (
          <div className="flex gap-1">
            <Btn variant="ghost" onClick={() => cameraRef.current?.click()} disabled={busy} data-testid="scan-photo-camera-btn" title="Take photo">
              <Camera className="w-4 h-4" /> {busy ? "…" : "Camera"}
            </Btn>
            <Btn variant="ghost" onClick={() => galleryRef.current?.click()} disabled={busy} data-testid="scan-photo-upload-btn" title="Upload from library">
              <ImagePlus className="w-4 h-4" /> Upload
            </Btn>
          </div>
        )}
        <input ref={cameraRef} type="file" accept="image/*" capture="environment" multiple className="hidden"
          onChange={(e) => { upload(e.target.files); e.target.value = ""; }} data-testid="scan-photo-camera-input" />
        <input ref={galleryRef} type="file" accept="image/*" multiple className="hidden"
          onChange={(e) => { upload(e.target.files); e.target.value = ""; }} data-testid="scan-photo-input" />
      </div>
      {list.length > 0 && (
        <div className="grid grid-cols-4 gap-2 mt-2">
          {list.map((p) => (
            <div key={p.id} className="relative aspect-square rounded overflow-hidden border border-[#FFD166]/20" data-testid={`scan-photo-thumb-${p.id}`}>
              <img src={`${BACKEND_URL}${p.url}`} alt="" className="w-full h-full object-cover" />
              <button type="button" onClick={() => remove(p.id)}
                className="absolute top-0.5 right-0.5 bg-black/70 hover:bg-red-500/80 text-white rounded-full p-1"
                data-testid={`scan-photo-delete-${p.id}`} aria-label="Delete photo">
                <Trash className="w-3 h-3" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/* ==================== ReviewSendPanel ================================== */
const ReviewSendPanel = ({ reservation }) => {
  const [sending, setSending] = useState(null);
  const [sentChannels, setSentChannels] = useState(new Set());

  const smsOk   = reservation.sms_allowed !== false && !!reservation.customer_phone;
  const emailOk = !!(reservation.customer_email || "").trim();

  if (!smsOk && !emailOk) return null;

  const send = async (channel) => {
    setSending(channel);
    try {
      const { data } = await axios.post(`${API}/admin/reservations/${reservation.id}/send-review`, { channel });
      if (data.sent_sms)   { toast.success("Review link sent via SMS ✓"); setSentChannels(p => new Set([...p, "sms"])); }
      if (data.sent_email) { toast.success("Review link sent via email ✓"); setSentChannels(p => new Set([...p, "email"])); }
      if (!data.sent_sms && !data.sent_email) toast.warning("Nothing sent — check phone/email on file.");
    } catch (e) {
      toast.error(fmtErr(e.response?.data?.detail) || "Send failed");
    } finally { setSending(null); }
  };

  return (
    <div className="mt-4 p-3 rounded-lg border border-[#FFD166]/20 bg-[#FFD166]/5" data-testid="review-send-panel">
      <div className="flex items-center gap-2 mb-3">
        <Send className="w-4 h-4 text-[#FFD166]" />
        <div className="text-xs font-medium text-[#F6F1E4]">Send Review Link</div>
        <span className="text-[10px] text-[#B9C8D5] ml-auto">Auto-sent day after pickup</span>
      </div>
      <div className="flex gap-2 flex-wrap">
        {smsOk && (
          <Btn variant="ghost" onClick={() => send("sms")} disabled={!!sending}
            className="flex-1 min-w-[80px]" data-testid="send-review-sms-btn">
            <MessageSquare className="w-3.5 h-3.5" />
            {sending === "sms" ? "Sending…" : sentChannels.has("sms") ? "SMS Sent ✓" : "SMS"}
          </Btn>
        )}
        {emailOk && (
          <Btn variant="ghost" onClick={() => send("email")} disabled={!!sending}
            className="flex-1 min-w-[80px]" data-testid="send-review-email-btn">
            <Mail className="w-3.5 h-3.5" />
            {sending === "email" ? "Sending…" : sentChannels.has("email") ? "Email Sent ✓" : "Email"}
          </Btn>
        )}
        {smsOk && emailOk && (
          <Btn onClick={() => send("both")} disabled={!!sending}
            className="flex-1 min-w-[80px]" data-testid="send-review-both-btn">
            <Send className="w-3.5 h-3.5" />
            {sending === "both" ? "Sending…" : "Both"}
          </Btn>
        )}
      </div>
    </div>
  );
};

/* ==================== TodayStopsPanel — manual customer select ========= */
const TodayStopsPanel = ({ onSelectStop, refreshTrigger = 0 }) => {
  const [open, setOpen]       = useState(false);
  const [stops, setStops]     = useState(null); // null = not loaded
  const [loading, setLoading] = useState(false);
  const today = new Date().toISOString().slice(0, 10);

  const load = async (force = false) => {
    if (stops !== null && !force) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/driver/my-stops`, { params: { day: today } });
      setStops(data.stops || []);
    } catch {
      setStops([]);
      toast.error("Could not load today's stops.");
    } finally { setLoading(false); }
  };

  // Auto-reload when parent signals a completion
  useEffect(() => {
    if (refreshTrigger > 0 && stops !== null) load(true);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshTrigger]);

  const refresh = (e) => { e.stopPropagation(); load(true); };
  const toggle  = () => { const next = !open; setOpen(next); if (next) load(); };

  const all        = stops || [];
  const deliveries = all.filter(s => s.kind === "delivery");
  const pickups    = all.filter(s => s.kind === "pickup");
  const total      = all.length;
  const doneCount  = all.filter(s =>
    (s.kind === "delivery" && s.delivered_at) ||
    (s.kind === "pickup"   && (s.returned_at || s.status === "completed"))
  ).length;
  const allDone    = total > 0 && doneCount === total;

  return (
    <Card className={`p-4 mb-4 transition-colors ${allDone ? "border-emerald-500/40" : ""}`} data-testid="stops-panel">
      <button type="button" onClick={toggle}
        className="w-full flex items-center justify-between"
        data-testid="stops-panel-toggle">
        <div className="flex items-center gap-2">
          <Users className={`w-4 h-4 ${allDone ? "text-emerald-400" : "text-[#FFD166]"}`} />
          <span className="text-sm font-medium text-[#F6F1E4]">Today's Assigned Stops</span>
          {stops && total > 0 && (
            <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${allDone ? "text-emerald-300 bg-emerald-500/15" : "text-[#B9C8D5] bg-white/10"}`}
              data-testid="stops-progress-badge">
              {doneCount}/{total}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {open && stops !== null && (
            <button type="button" onClick={refresh} disabled={loading}
              className="text-[#B9C8D5] hover:text-[#FFD166] p-1 rounded-full hover:bg-white/10 transition-colors"
              data-testid="stops-refresh-btn" title="Refresh stops">
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
            </button>
          )}
          <ChevronDown className={`w-4 h-4 text-[#B9C8D5] transition-transform ${open ? "rotate-180" : ""}`} />
        </div>
      </button>

      {open && (
        <div className="mt-3">
          {loading && <div className="text-xs text-[#B9C8D5] animate-pulse">Loading stops…</div>}
          {stops !== null && total === 0 && (
            <div className="text-xs text-[#B9C8D5]">No stops assigned to you for today.</div>
          )}

          {deliveries.length > 0 && (
            <div className="mb-3">
              <div className="text-[10px] font-semibold text-green-400 uppercase tracking-wide mb-1.5">
                Deliveries ({deliveries.filter(s => s.delivered_at).length}/{deliveries.length})
              </div>
              <div className="space-y-1.5">
                {deliveries.map(s => {
                  const done = !!s.delivered_at;
                  return (
                    <button key={s.reservation_id} type="button"
                      onClick={() => { onSelectStop(s.reservation_id); setOpen(false); }}
                      className={`w-full text-left p-2.5 rounded-lg border transition-colors ${
                        done
                          ? "border-emerald-500/40 bg-emerald-500/8 opacity-75 hover:opacity-100 hover:border-emerald-400/60"
                          : "border-[#FFD166]/20 bg-black/20 hover:border-[#FFD166]/50 hover:bg-[#FFD166]/5"
                      }`}
                      data-testid={`stops-delivery-${s.reservation_id}`}>
                      <div className="flex items-center justify-between gap-2">
                        <span className={`text-sm font-medium truncate ${done ? "text-[#F6F1E4]/70" : "text-[#F6F1E4]"}`}>
                          {s.customer_name}
                        </span>
                        {done && (
                          <span className="flex items-center gap-1 text-[10px] font-medium text-emerald-400 shrink-0"
                            data-testid={`stops-delivery-done-${s.reservation_id}`}>
                            <CheckCircle2 className="w-3 h-3" /> Delivered
                          </span>
                        )}
                      </div>
                      <div className="text-[10px] text-[#B9C8D5] mt-0.5">{s.address}</div>
                      <div className="text-[10px] text-[#B9C8D5]">
                        {s.line_items.map(li => `${li.name}${li.quantity > 1 ? ` ×${li.quantity}` : ""}`).join(" · ")}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {pickups.length > 0 && (
            <div>
              <div className="text-[10px] font-semibold text-amber-400 uppercase tracking-wide mb-1.5">
                Pickups ({pickups.filter(s => s.returned_at || s.status === "completed").length}/{pickups.length})
              </div>
              <div className="space-y-1.5">
                {pickups.map(s => {
                  const done = !!(s.returned_at || s.status === "completed");
                  return (
                    <button key={`pk-${s.reservation_id}`} type="button"
                      onClick={() => { onSelectStop(s.reservation_id); setOpen(false); }}
                      className={`w-full text-left p-2.5 rounded-lg border transition-colors ${
                        done
                          ? "border-emerald-500/40 bg-emerald-500/8 opacity-75 hover:opacity-100 hover:border-emerald-400/60"
                          : "border-amber-500/20 bg-black/20 hover:border-amber-400/40 hover:bg-amber-500/5"
                      }`}
                      data-testid={`stops-pickup-${s.reservation_id}`}>
                      <div className="flex items-center justify-between gap-2">
                        <span className={`text-sm font-medium truncate ${done ? "text-[#F6F1E4]/70" : "text-[#F6F1E4]"}`}>
                          {s.customer_name}
                        </span>
                        {done && (
                          <span className="flex items-center gap-1 text-[10px] font-medium text-emerald-400 shrink-0"
                            data-testid={`stops-pickup-done-${s.reservation_id}`}>
                            <CheckCircle2 className="w-3 h-3" /> Picked Up
                          </span>
                        )}
                      </div>
                      <div className="text-[10px] text-[#B9C8D5] mt-0.5">{s.address}</div>
                      <div className="text-[10px] text-[#B9C8D5]">
                        {s.line_items.map(li => `${li.name}${li.quantity > 1 ? ` ×${li.quantity}` : ""}`).join(" · ")}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* All-done banner */}
          {allDone && total > 0 && (
            <div className="mt-3 p-2 rounded-lg border border-emerald-500/30 bg-emerald-500/8 flex items-center gap-2"
              data-testid="stops-all-done-banner">
              <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />
              <span className="text-xs text-emerald-300 font-medium">All stops complete for today!</span>
            </div>
          )}
        </div>
      )}
    </Card>
  );
};

/* ==================== PickupFlow — check-in each assigned item ========= */
const PickupFlow = ({ reservation, onCompleted }) => {
  const [checkedIn, setCheckedIn] = useState(new Set());
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(reservation.status === "completed" || !!reservation.returned_at);
  const [photos, setPhotos] = useState(reservation.photos || []);

  const assigned = reservation.assigned_unit_details || [];
  const rentedUnits = assigned.filter(u => u.status === "rented");
  const returnedCount = assigned.filter(u => u.status !== "rented").length + checkedIn.size;
  const allDone = assigned.length > 0 && rentedUnits.every(u => checkedIn.has(u.id));

  const checkInUnit = async (unitId) => {
    try {
      const { data } = await axios.post(`${API}/admin/inventory/${unitId}/checkin`);
      setCheckedIn(prev => new Set([...prev, unitId]));
      if (data.completed_reservation) {
        setDone(true);
        toast.success("All items returned — reservation complete ✓");
        onCompleted?.();
      } else {
        toast.success("Item checked in ✓");
      }
    } catch (e) { toast.error(fmtErr(e.response?.data?.detail)); }
  };

  const markAllReturned = async () => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/admin/reservations/${reservation.id}/return`);
      setDone(true);
      toast.success("Reservation completed ✓");
      onCompleted?.();
    } catch (e) { toast.error(fmtErr(e.response?.data?.detail)); }
    finally { setSubmitting(false); }
  };

  return (
    <div data-testid="pickup-flow">
      {/* Photos */}
      <ScanPhotos reservation={{ ...reservation, photos }} mode="pickup"
        onChange={(next) => setPhotos(next)} />

      {/* Assigned units — check in one by one */}
      {assigned.length > 0 && (
        <div className="mb-4">
          <div className="flex items-center justify-between mb-2">
            <span className="eyebrow">Check in items ({returnedCount}/{assigned.length})</span>
            <span className={`text-xs ${allDone || done ? "text-emerald-300" : "text-[#F4A93A]"}`}>
              {allDone || done ? "All returned ✓" : "Tap item to check in"}
            </span>
          </div>
          <div className="space-y-2">
            {assigned.map(u => {
              const isRented    = u.status === "rented";
              const isReturned  = !isRented || checkedIn.has(u.id);
              return (
                <div key={u.id}
                  className={`flex items-center gap-3 p-2.5 rounded-lg border transition-colors ${isReturned ? "border-emerald-500/30 bg-emerald-500/8" : "border-[#FFD166]/15 bg-black/25"}`}
                  data-testid={`pickup-unit-row-${u.id}`}>
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${isReturned ? "border-emerald-400 bg-emerald-500/20" : "border-[#B9C8D5]/40"}`}>
                    {isReturned && <CheckCircle2 className="w-3 h-3 text-emerald-400" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-[#F6F1E4] truncate">{u.product_name}</div>
                    <div className="text-[10px] font-mono text-[#B9C8D5]">SKU: {u.barcode}</div>
                  </div>
                  {isRented && !checkedIn.has(u.id) ? (
                    <Btn variant="ghost" onClick={() => checkInUnit(u.id)}
                      className="shrink-0 text-xs !py-1 !px-2"
                      data-testid={`pickup-checkin-unit-${u.id}`}>
                      <CheckCircle2 className="w-3.5 h-3.5" /> Check In
                    </Btn>
                  ) : (
                    <span className="text-[10px] text-emerald-300 uppercase font-medium shrink-0">Returned ✓</span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Complete button */}
      {!done && (
        <Btn onClick={markAllReturned} disabled={submitting} className="w-full mb-3"
          data-testid="scan-mark-returned-btn">
          <CheckCircle2 className="w-4 h-4" />
          {submitting ? "Marking returned…" : "Mark all returned"}
        </Btn>
      )}

      {done && (
        <div className="p-3 rounded-lg border border-emerald-500/30 bg-emerald-500/5 mb-3 text-center">
          <CheckCircle2 className="w-5 h-5 text-emerald-400 mx-auto mb-1" />
          <div className="text-sm font-medium text-emerald-300">Pickup complete!</div>
          <div className="text-[10px] text-[#B9C8D5] mt-0.5">All items returned</div>
        </div>
      )}

      {/* Review send — available after pickup is done */}
      {(done || reservation.status === "completed") && (
        <ReviewSendPanel reservation={reservation} />
      )}
    </div>
  );
};

/* ==================== DeliveryFlow — the guided drop-off workflow ======= */
const DeliveryFlow = ({ reservation, onScanClick, onCompleted }) => {
  const lineItems   = reservation.line_items || [];
  const totalNeeded = lineItems.reduce((n, li) => n + (li.quantity || 0), 0);
  const [scanned,   setScanned]   = useState([]);
  const [available, setAvailable] = useState({});
  const [expanded,  setExpanded]  = useState({});
  const [skuFilter, setSkuFilter] = useState({});   // { product_id: string }
  const [photos,    setPhotos]    = useState(reservation.photos || []);
  const [location,  setLocation]  = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [confirmed, setConfirmed] = useState(new Set());

  const preassigned   = reservation.assigned_unit_details;
  const isFullyStaged = preassigned && preassigned.length >= totalNeeded;

  useEffect(() => {
    const pre = (preassigned || []).map(u => ({
      unit_id: u.id, product_id: u.product_id, product_name: u.product_name, barcode: u.barcode, preexisting: true,
    }));
    setScanned(pre);
    setConfirmed(new Set());
  }, [reservation.id, preassigned]);

  useEffect(() => { setPhotos(reservation.photos || []); }, [reservation.photos]);

  useEffect(() => {
    const handler = (e) => {
      const unit = e.detail?.unit;
      if (!unit) return;
      const matchingLine = lineItems.find(li => li.product_id === unit.product_id);
      if (!matchingLine) { toast.error(`${unit.product_name || "Unit"} isn't on this reservation.`); return; }
      if (unit.status !== "available" && unit.status !== "rented") { toast.error(`Unit ${unit.barcode} is ${unit.status} — pick another.`); return; }
      addUnitInternal(unit, matchingLine.product_id, matchingLine.name);
    };
    window.addEventListener("driver-scan-attach", handler);
    return () => window.removeEventListener("driver-scan-attach", handler);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scanned, lineItems]);

  const loadAvailable = useCallback(async (productId) => {
    if (available[productId]) return;
    try {
      const { data } = await axios.get(`${API}/admin/inventory`, { params: { product_id: productId } });
      setAvailable(prev => ({ ...prev, [productId]: (data.units || []).filter(u => u.status === "available") }));
    } catch (e) { toast.error(fmtErr(e.response?.data?.detail) || "Couldn't load inventory"); }
  }, [available]);

  const addUnitInternal = (unit, productId, productName) => {
    if (scanned.some(s => s.unit_id === unit.id)) { toast("Already scanned", { icon: "✓" }); return; }
    const need = lineItems.filter(li => li.product_id === productId).reduce((n, li) => n + (li.quantity || 0), 0);
    const have = scanned.filter(s => s.product_id === productId).length;
    if (have >= need) { toast.error(`${productName}: already have all ${need}`); return; }
    setScanned(prev => [...prev, { unit_id: unit.id, product_id: productId, product_name: productName, barcode: unit.barcode }]);
    toast.success(`${productName} · ${unit.barcode} added ✓`);
  };

  const removeUnit   = (unit_id) => setScanned(prev => prev.filter(s => s.unit_id !== unit_id));
  const toggleConfirm = (unit_id) => {
    setConfirmed(prev => { const next = new Set(prev); next.has(unit_id) ? next.delete(unit_id) : next.add(unit_id); return next; });
  };

  const scanCount     = scanned.length;
  const itemsVerified = isFullyStaged
    ? scanned.length >= totalNeeded && scanned.every(s => confirmed.has(s.unit_id))
    : scanned.length >= totalNeeded;
  const hasPhoto   = photos.some(p => p.kind === "delivery");
  const canDeliver = itemsVerified && hasPhoto && !!location;

  const submit = async () => {
    if (!canDeliver) return;
    setSubmitting(true);
    try {
      const newUnitIds = scanned.filter(s => !s.preexisting).map(s => s.unit_id);
      const { data } = await axios.post(`${API}/admin/reservations/${reservation.id}/deliver`, {
        unit_ids: newUnitIds, delivery_location: location,
      });
      toast.success(`Delivered ✓ ${data.sms_sent ? "· SMS sent to customer" : ""}`);
      onCompleted?.();
    } catch (e) { toast.error(fmtErr(e.response?.data?.detail) || "Mark delivered failed"); }
    finally { setSubmitting(false); }
  };

  return (
    <div data-testid="delivery-flow">
      {/* Step 1: items */}
      {isFullyStaged ? (
        <div className="mb-4" data-testid="delivery-staged-verify">
          <div className="flex items-center justify-between mb-2">
            <span className="eyebrow">Verify staged items ({confirmed.size}/{scanned.length})</span>
            <span className={`text-xs ${itemsVerified ? "text-emerald-300" : "text-[#F4A93A]"}`}>
              {itemsVerified ? "All confirmed ✓" : "Tap each item to confirm loaded"}
            </span>
          </div>
          <div className="space-y-2">
            {scanned.map(s => {
              const isOk = confirmed.has(s.unit_id);
              return (
                <button key={s.unit_id} type="button" onClick={() => toggleConfirm(s.unit_id)}
                  className={`w-full flex items-center gap-3 p-3 rounded-lg border text-left transition-colors ${isOk ? "border-emerald-500/50 bg-emerald-500/10" : "border-[#FFD166]/25 bg-black/25 hover:border-[#FFD166]/50"}`}
                  data-testid={`delivery-verify-${s.unit_id}`}>
                  <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-colors ${isOk ? "border-emerald-400 bg-emerald-500/25" : "border-[#B9C8D5]/40"}`}>
                    {isOk && <CheckCircle2 className="w-4 h-4 text-emerald-400" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-[#F6F1E4]">{s.product_name}</div>
                    <div className="text-[11px] font-mono text-[#B9C8D5]">SKU: {s.barcode}</div>
                  </div>
                  <span className={`text-[10px] font-medium uppercase ${isOk ? "text-emerald-300" : "text-[#B9C8D5]"}`}>
                    {isOk ? "Loaded ✓" : "Tap to confirm"}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      ) : (
        <div className="mb-4">
          <div className="flex items-center justify-between mb-2">
            <span className="eyebrow">Scan items ({scanCount}/{totalNeeded})</span>
            <span className={`text-xs ${scanCount >= totalNeeded ? "text-emerald-300" : "text-[#F4A93A]"}`}>
              {scanCount >= totalNeeded ? "All items ready ✓" : "Scan or pick each unit"}
            </span>
          </div>
          <Btn onClick={onScanClick} variant="ghost" className="w-full mb-3" data-testid="delivery-scan-camera-btn">
            <Camera className="w-4 h-4" /> Scan a unit barcode
          </Btn>
          <div className="space-y-2">
            {lineItems.map(li => {
              const scannedForItem = scanned.filter(s => s.product_id === li.product_id);
              const remaining  = (li.quantity || 0) - scannedForItem.length;
              const isExpanded = expanded[li.product_id];
              const skuQ       = (skuFilter[li.product_id] || "").toLowerCase();
              return (
                <div key={li.product_id} className="rounded-lg border border-[#FFD166]/15 bg-black/25" data-testid={`delivery-item-${li.product_id}`}>
                  <div className="flex items-center justify-between p-3">
                    <div className="min-w-0 flex-1">
                      <div className="text-sm text-[#F6F1E4] truncate">{li.name}</div>
                      <div className="text-[11px] text-[#B9C8D5]">
                        Needed: <b className="text-[#F6F1E4]">{li.quantity}</b> ·
                        Scanned: <b className={remaining === 0 ? "text-emerald-300" : "text-[#F4A93A]"}>{scannedForItem.length}</b>
                      </div>
                    </div>
                    {remaining > 0 && (
                      <button
                        onClick={() => { setExpanded(e => ({ ...e, [li.product_id]: !isExpanded })); if (!isExpanded) loadAvailable(li.product_id); }}
                        className="text-xs px-3 py-1.5 rounded-full border border-[#FFD166]/30 text-[#FFD166] hover:bg-[#FFD166]/10 inline-flex items-center gap-1"
                        data-testid={`delivery-item-pick-${li.product_id}`}>
                        Pick by SKU <ChevronDown className={`w-3 h-3 transition-transform ${isExpanded ? "rotate-180" : ""}`} />
                      </button>
                    )}
                  </div>
                  {scannedForItem.length > 0 && (
                    <div className="px-3 pb-2 flex flex-wrap gap-1.5">
                      {scannedForItem.map(s => (
                        <span key={s.unit_id} className="text-[10px] font-mono bg-emerald-500/15 border border-emerald-500/40 text-emerald-300 px-2 py-0.5 rounded-full inline-flex items-center gap-1" data-testid={`delivery-unit-chip-${s.unit_id}`}>
                          {s.barcode}
                          {!s.preexisting && (
                            <button onClick={() => removeUnit(s.unit_id)} className="hover:text-red-300 ml-0.5" aria-label="Remove">×</button>
                          )}
                        </span>
                      ))}
                    </div>
                  )}
                  {isExpanded && remaining > 0 && (
                    <div className="px-3 pb-3">
                      {/* SKU search filter */}
                      <Input
                        type="text"
                        placeholder="Filter by SKU / barcode…"
                        value={skuFilter[li.product_id] || ""}
                        onChange={e => setSkuFilter(prev => ({ ...prev, [li.product_id]: e.target.value }))}
                        className="text-xs mb-2"
                        data-testid={`delivery-sku-filter-${li.product_id}`}
                      />
                      <div className="space-y-1 max-h-48 overflow-y-auto">
                        {!available[li.product_id] && <div className="text-xs text-[#B9C8D5]">Loading available units…</div>}
                        {available[li.product_id]?.length === 0 && <div className="text-xs text-[#F4A93A]">No available units for this product!</div>}
                        {available[li.product_id]
                          ?.filter(u => !scanned.some(s => s.unit_id === u.id))
                          .filter(u => !skuQ || u.barcode.toLowerCase().includes(skuQ))
                          .map(u => (
                            <button key={u.id} onClick={() => addUnitInternal(u, li.product_id, li.name)}
                              className="w-full text-left text-xs font-mono bg-black/30 hover:bg-[#FFD166]/10 border border-[#FFD166]/15 hover:border-[#FFD166]/50 px-2 py-1.5 rounded"
                              data-testid={`delivery-pick-unit-${u.id}`}>
                              {u.barcode}
                            </button>
                          ))
                        }
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Step 2: photo */}
      <ScanPhotos reservation={{ ...reservation, photos }} mode="delivery" onChange={setPhotos} />

      {/* Step 3: location */}
      <div className={`mb-4 p-3 rounded-lg border ${!location ? "border-[#F4A93A]/60 bg-[#F4A93A]/5" : "border-[#FFD166]/25 bg-black/30"}`} data-testid="delivery-location-picker">
        <div className="flex items-center gap-2 mb-2">
          <MapPin className="w-4 h-4 text-[#FFD166]" />
          <div className="text-xs font-medium text-[#F6F1E4]">Where was it delivered?</div>
        </div>
        <div className="grid grid-cols-2 gap-2">
          {DELIVERY_LOCATIONS.map(opt => (
            <button key={opt.value} type="button" onClick={() => setLocation(opt.value)}
              className={`text-xs px-3 py-2 rounded-lg border transition-colors ${location === opt.value ? "border-[#FFD166] bg-[#FFD166]/10 text-[#FFD166]" : "border-[#B9C8D5]/25 text-[#F6F1E4]/85 hover:border-[#FFD166]/40"}`}
              data-testid={`delivery-location-${opt.value}`}>
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* Step 4: submit */}
      <Btn onClick={submit} disabled={!canDeliver || submitting} className="w-full" data-testid="scan-deliver-btn">
        <CheckCircle2 className="w-4 h-4" />
        {submitting ? "Marking delivered…" : "Mark delivered"}
      </Btn>
      {!canDeliver && (
        <div className="mt-2 text-[10px] text-[#F4A93A] space-y-0.5">
          {isFullyStaged && !itemsVerified && <div>• Tap each staged item to confirm it's loaded</div>}
          {!isFullyStaged && scanned.length < totalNeeded && <div>• Scan or pick every unit ({scanCount}/{totalNeeded})</div>}
          {!hasPhoto && <div>• Take at least one drop-off photo</div>}
          {!location && <div>• Pick where it was delivered</div>}
        </div>
      )}
    </div>
  );
};

/* ==================== DriverScan main component ========================= */
export const DriverScan = () => {
  const { user } = useAuth();
  const backHref = user?.role === "employee" ? "/employee" : "/admin/reservations";
  const [sp, setSp]           = useSearchParams();
  const mode                  = (sp.get("mode") || "auto").toLowerCase();
  const [scannerOpen, setScannerOpen] = useState(false);
  const [manualCode, setManualCode]   = useState("");
  const [result, setResult]           = useState(null);
  const [loading, setLoading]         = useState(false);
  const [historyStack, setHistoryStack] = useState([]);
  const [contactResId, setContactResId] = useState(null);
  const [scanContext, setScanContext]   = useState("global");
  const [stopsRefreshKey, setStopsRefreshKey] = useState(0);

  const bumpStops = () => setStopsRefreshKey(k => k + 1);

  const setMode = (m) => setSp({ mode: m }, { replace: true });

  const lookup = useCallback(async (code) => {
    if (!code || !code.trim()) return;
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/admin/scan`, { code: code.trim(), mode });
      setHistoryStack((h) => [{ code: code.trim(), kind: data.kind, at: new Date() }, ...h].slice(0, 8));
      if (data.kind === "not_found") { toast.error(`Barcode "${code.trim()}" not recognized.`); return; }
      if (scanContext === "deliver-attach" && data.kind === "unit" && result?.kind === "reservation") {
        window.dispatchEvent(new CustomEvent("driver-scan-attach", { detail: { unit: data.unit } }));
        return;
      }
      setResult(data);
      if (data.kind === "unit")        toast.success(`✓ ${data.unit.product_name || "Unit"} · ${data.unit.status}`);
      else if (data.kind === "reservation") toast.success(`✓ ${data.reservation.customer_name}`);
    } catch (e) {
      toast.error(fmtErr(e.response?.data?.detail) || "Lookup failed.");
    } finally { setLoading(false); }
  }, [mode, result, scanContext]);

  const onScannerResult = useCallback((decoded) => { setScannerOpen(false); lookup(decoded); }, [lookup]);
  const manualSubmit    = (e) => { e.preventDefault(); lookup(manualCode); setManualCode(""); };

  const checkin = async (unitId) => {
    try {
      const { data } = await axios.post(`${API}/admin/inventory/${unitId}/checkin`);
      toast.success(data.completed_reservation ? "Unit returned · reservation completed ✓" : "Unit returned ✓");
      setResult(null);
    } catch (e) { toast.error(fmtErr(e.response?.data?.detail)); }
  };

  const openScannerForAttach = () => { setScanContext("deliver-attach"); setScannerOpen(true); };
  const onDeliverCompleted   = () => { setResult(null); setScanContext("global"); bumpStops(); };

  return (
    <div className="min-h-[80vh]" data-testid="admin-driver-scan">
      <div className="flex items-center justify-between mb-4">
        <div>
          <Link to={backHref} className="text-xs text-[#B9C8D5] hover:text-[#FFD166] inline-flex items-center gap-1 mb-1">
            <ArrowLeft className="w-3 h-3" /> Back
          </Link>
          <h1 className="font-serif text-3xl">Scan &amp; Check In</h1>
        </div>
      </div>

      {/* Mode tabs */}
      <div className="flex flex-wrap gap-2 mb-5" data-testid="scan-mode-tabs">
        {[{ v: "auto", label: "🎯 Auto" }, { v: "delivery", label: "🚚 Delivery" }, { v: "pickup", label: "📦 Pickup" }].map((m) => (
          <button key={m.v} type="button" onClick={() => setMode(m.v)}
            className={`px-4 py-2 rounded-full text-sm border transition ${mode === m.v ? "border-[#FFD166] bg-[#FFD166]/10 text-[#FFD166]" : "border-[#B9C8D5]/30 text-[#B9C8D5] hover:border-[#FFD166]/40"}`}
            data-testid={`scan-mode-${m.v}`}>{m.label}
          </button>
        ))}
      </div>

      {/* Today's stops — manual customer select (damaged-label fallback) */}
      <TodayStopsPanel onSelectStop={(resId) => lookup(resId)} refreshTrigger={stopsRefreshKey} />

      {/* Scanner + manual input */}
      <Card className="p-5 mb-4">
        <Btn onClick={() => { setScanContext("global"); setScannerOpen(true); }} className="w-full h-14 text-base" data-testid="scan-open-camera-btn">
          <Camera className="w-5 h-5" /> Open camera scanner
        </Btn>
        <form onSubmit={manualSubmit} className="mt-4 flex gap-2 items-end" data-testid="scan-manual-form">
          <div className="flex-1">
            <label className="text-[10px] uppercase text-[#B9C8D5]">Or type the code (short EGR-XXXXXX, UUID, or unit barcode)</label>
            <Input value={manualCode} onChange={(e) => setManualCode(e.target.value)} placeholder="EGR-…" className="mt-1 tracking-wider" data-testid="scan-manual-input" />
          </div>
          <Btn disabled={!manualCode.trim() || loading} data-testid="scan-manual-submit"><Keyboard className="w-4 h-4" /> Look up</Btn>
        </form>
      </Card>

      {/* Unit result */}
      {result && result.kind === "unit" && (
        <Card className="p-5 mb-4 border-emerald-500/40" data-testid="scan-result-unit">
          <div className="flex items-start gap-3 mb-4">
            <Package className="w-6 h-6 text-[#FFD166]" />
            <div className="flex-1">
              <div className="text-[10px] uppercase tracking-widest text-[#B9C8D5]">Inventory Unit</div>
              <div className="font-serif text-2xl text-[#F6F1E4]">{result.unit.product_name}</div>
              <div className="text-xs text-[#B9C8D5] mt-1">SKU <span className="text-[#F6F1E4] font-mono">{result.unit.barcode}</span> · status <span className={`ml-1 ${result.unit.status === "available" ? "text-emerald-300" : result.unit.status === "rented" ? "text-orange-300" : "text-[#B9C8D5]"}`}>{result.unit.status}</span></div>
            </div>
          </div>
          {result.reservation && (
            <div className="rounded-lg border border-[#FFD166]/20 bg-black/30 p-3 mb-4">
              <div className="text-[10px] uppercase text-[#B9C8D5]">Currently rented to</div>
              <div className="font-serif text-lg text-[#F6F1E4]">{result.reservation.customer_name}</div>
              <div className="text-[11px] text-[#B9C8D5]">{result.reservation.delivery_date} → {result.reservation.pickup_date} · {result.reservation.delivery_resort}</div>
            </div>
          )}
          {result.suggested_action === "checkin" ? (
            <Btn onClick={() => checkin(result.unit.id)} className="w-full" data-testid="scan-checkin-btn">
              <CheckCircle2 className="w-4 h-4" /> Mark this unit returned
            </Btn>
          ) : (
            <div className="text-xs text-[#B9C8D5] italic">Unit is not currently rented — nothing to check in.</div>
          )}
        </Card>
      )}

      {/* Reservation result */}
      {result && result.kind === "reservation" && (
        <Card className="p-5 mb-4 border-[#FFD166]/40" data-testid="scan-result-reservation">
          <div className="flex items-start gap-3 mb-4">
            <User className="w-6 h-6 text-[#FFD166]" />
            <div className="flex-1">
              <div className="text-[10px] uppercase tracking-widest text-[#B9C8D5]">
                Reservation · {result.reservation.short_code || result.reservation.id.slice(0, 8)}
              </div>
              <div className="font-serif text-2xl text-[#F6F1E4]">{result.reservation.customer_name}</div>
              <div className="text-xs text-[#B9C8D5] mt-1">{result.reservation.delivery_resort}</div>
              <div className="mt-2 flex flex-wrap gap-2 items-center" data-testid="scan-customer-contact">
                <button type="button" onClick={() => setContactResId(result.reservation.id)}
                  className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-full border border-[#FFD166]/50 bg-[#FFD166]/10 text-[#FFD166] hover:bg-[#FFD166]/20 font-medium"
                  data-testid="scan-contact-customer">
                  💬 Contact Customer
                </button>
                {result.reservation.sms_allowed === false && (
                  <span className="text-[10px] text-[#F4A93A]">Email only — no SMS consent</span>
                )}
                {result.reservation.customer_phone && (
                  <a href={`tel:${result.reservation.customer_phone}`}
                    className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-full border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10"
                    data-testid="scan-customer-call">
                    📞 {result.reservation.customer_phone}
                  </a>
                )}
                {result.reservation.customer_email && (
                  <a href={`mailto:${result.reservation.customer_email}`}
                    className="inline-flex items-center gap-1 text-xs text-[#B9C8D5] hover:text-[#FFD166]"
                    data-testid="scan-customer-email">
                    ✉️ {result.reservation.customer_email}
                  </a>
                )}
              </div>
              {contactResId && (
                <ConversationPanel reservationId={contactResId} customerName={result.reservation.customer_name} onClose={() => setContactResId(null)} />
              )}
            </div>
          </div>

          <div className="rounded-lg border border-[#FFD166]/20 bg-black/30 p-3 mb-4 text-sm">
            <div><span className="text-[#FFD166]">Deliver</span> {result.reservation.delivery_date} · {formatSlot(result.reservation.delivery_slot)}</div>
            <div><span className="text-[#FFD166]">Pickup</span>  {result.reservation.pickup_date} · {formatSlot(result.reservation.pickup_slot)}</div>
            <div className="text-xs text-[#B9C8D5] mt-2">Status <b>{result.reservation.status}</b> · {result.reservation.payment_status}</div>
          </div>

          {result.suggested_action === "deliver" ? (
            <DeliveryFlow reservation={result.reservation} onScanClick={openScannerForAttach} onCompleted={onDeliverCompleted} />
          ) : (
            <PickupFlow
              reservation={result.reservation}
              onCompleted={() => {
                // Refresh so the review panel shows + update stop badge
                setResult(r => r ? { ...r, reservation: { ...r.reservation, status: "completed" } } : r);
                bumpStops();
              }}
            />
          )}

          {/* For completed reservations where suggested_action isn't pickup — show review option */}
          {result.suggested_action !== "deliver" && result.suggested_action !== "pickup" &&
            result.reservation.status === "completed" && (
            <ReviewSendPanel reservation={result.reservation} />
          )}
        </Card>
      )}

      {/* Recent scans */}
      {historyStack.length > 0 && (
        <Card className="p-4">
          <div className="eyebrow mb-2">Recent scans</div>
          <div className="text-xs space-y-1">
            {historyStack.map((h) => (
              <div key={`${h.code}-${h.at.getTime()}`} className="flex items-center justify-between text-[#B9C8D5]">
                <span className="font-mono truncate max-w-[65%]">{h.code}</span>
                <span className="text-[10px]">{h.kind} · {h.at.toLocaleTimeString()}</span>
              </div>
            ))}
          </div>
        </Card>
      )}

      <ScannerModal open={scannerOpen} onClose={() => { setScannerOpen(false); setScanContext("global"); }} onResult={onScannerResult} />
    </div>
  );
};

export default DriverScan;
